import { FC, useEffect, useRef } from "react";

import { Box } from "@mui/material";

import { getCanvasAnimationLayerSx } from "./canvasAnimationLayerSx";
import type { CanvasAnimationLayoutVariant, CanvasBackgroundTheme } from "./canvasBackgroundTheme";
import { resizeCanvasKeepingPositions } from "./resizeCanvasKeepingPositions";
import { resolveCanvasSurfaceSize } from "./resolveCanvasSurfaceSize";
import { subscribeVisibilityAwareAnimationLoop } from "./subscribeVisibilityAwareAnimationLoop";

/** Fraction of particles that get a glow halo in light themes (0 = none, 1 = all) */
const LIGHT_THEME_GLOW_RATIO = 0.5;

export type SparklesAnimationProps = {
  hue: number;
  theme: CanvasBackgroundTheme;
  variant?: CanvasAnimationLayoutVariant;
  isMobile?: boolean;
  frameStride?: number;
};

type SparkleParticle = {
  x: number;
  y: number;
  r: number;
  opacity: number;
  speedY: number;
  drift: number;
  twinkleSpeed: number;
  twinkleOff: number;
  rot: number;
  rotSpeed: number;
  hasGlow: boolean;
};

export const SparklesAnimation: FC<SparklesAnimationProps> = ({
  hue,
  theme,
  variant = "viewportFixed",
  isMobile = false,
  frameStride = 1,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const surfaceMaybe = canvasRef.current;
    if (!surfaceMaybe) {
      return;
    }
    const contextMaybe = surfaceMaybe.getContext("2d");
    if (!contextMaybe) {
      return;
    }

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (motionQuery.matches) {
      return;
    }

    const surface: HTMLCanvasElement = surfaceMaybe;
    const graphics: CanvasRenderingContext2D = contextMaybe;

    function tc(alpha: number): string {
      switch (theme) {
        case "dark":
          return `hsla(${hue}, 68%, 82%, ${alpha * 0.9})`;
        case "light":
          return `hsla(${hue}, 55%, 58%, ${alpha * 0.85})`;
        case "darkdull":
          return `hsla(${hue}, 22%, 72%, ${alpha * 0.78})`;
        case "lightdull":
          return `hsla(${hue}, 20%, 58%, ${alpha * 0.72})`;
        default:
          return `hsla(${hue}, 68%, 82%, ${alpha * 0.9})`;
      }
    }

    function tg(alpha: number): string {
      switch (theme) {
        case "dark":
          return `hsla(${hue}, 80%, 88%, ${alpha * 0.2})`;
        case "light":
          return `hsla(${hue}, 55%, 78%, ${alpha * 0.28})`;
        case "darkdull":
          return `hsla(${hue}, 28%, 75%, ${alpha * 0.15})`;
        case "lightdull":
          return `hsla(${hue}, 18%, 82%, ${alpha * 0.22})`;
        default:
          return `hsla(${hue}, 80%, 88%, ${alpha * 0.2})`;
      }
    }

    function gradEnd(): string {
      switch (theme) {
        case "dark":
          return `hsla(${hue}, 80%, 88%, 0)`;
        case "light":
          return `hsla(${hue}, 55%, 78%, 0)`;
        case "darkdull":
          return `hsla(${hue}, 28%, 75%, 0)`;
        case "lightdull":
          return `hsla(${hue}, 18%, 82%, 0)`;
        default:
          return `hsla(${hue}, 80%, 88%, 0)`;
      }
    }

    function resizeCanvas(): void {
      const { width, height } = resolveCanvasSurfaceSize(surface);
      surface.width = width;
      surface.height = height;
    }

    resizeCanvas();

    const isLightTheme = theme === "light" || theme === "lightdull";

    function mkSparkle(): SparkleParticle {
      return {
        x: Math.random() * surface.width,
        y: Math.random() * surface.height,
        r: isLightTheme ? Math.random() * 5 + 3 : Math.random() * 4 + 2,
        opacity: Math.random() * 0.45 + 0.55,
        speedY: Math.random() * 0.3 + 0.1,
        drift: (Math.random() - 0.5) * 0.15,
        twinkleSpeed: Math.random() * 0.028 + 0.008,
        twinkleOff: Math.random() * Math.PI * 2,
        rot: Math.random() * Math.PI * 2,
        rotSpeed: (Math.random() - 0.5) * 0.01,
        hasGlow: isLightTheme ? Math.random() < LIGHT_THEME_GLOW_RATIO : true,
      };
    }

    const sparkles: SparkleParticle[] = Array.from({ length: isMobile ? 84 : 120 }, () => mkSparkle());

    function onResize(): void {
      resizeCanvasKeepingPositions(surface, sparkles);
    }

    window.addEventListener("resize", onResize);

    function drawStarShape(x: number, y: number, radius: number, rotation: number): void {
      const outer = radius;
      const inner = radius * 0.1;
      graphics.beginPath();
      for (let index = 0; index < 8; index++) {
        const angle = (index / 8) * Math.PI * 2 + rotation;
        const distance = index % 2 === 0 ? outer : inner;
        const px = x + Math.cos(angle) * distance;
        const py = y + Math.sin(angle) * distance;
        if (index === 0) {
          graphics.moveTo(px, py);
        } else {
          graphics.lineTo(px, py);
        }
      }
      graphics.closePath();
    }

    let frame = 0;

    const stopAnimationLoop = subscribeVisibilityAwareAnimationLoop({ frameStride }, (frameSteps) => {
      graphics.clearRect(0, 0, surface.width, surface.height);
      frame += frameSteps;

      for (const particle of sparkles) {
        const twinkle = Math.sin(frame * particle.twinkleSpeed + particle.twinkleOff) * 0.3 + 0.7;
        const alpha = particle.opacity * twinkle;

        if (particle.hasGlow) {
          const glowRadius = particle.r * 2.5;
          const gradient = graphics.createRadialGradient(particle.x, particle.y, 0, particle.x, particle.y, glowRadius);
          gradient.addColorStop(0, tg(alpha));
          gradient.addColorStop(1, gradEnd());
          graphics.beginPath();
          graphics.arc(particle.x, particle.y, glowRadius, 0, Math.PI * 2);
          graphics.fillStyle = gradient;
          graphics.fill();
        }

        drawStarShape(particle.x, particle.y, particle.r * (0.8 + twinkle * 0.2), particle.rot);
        graphics.fillStyle = tc(alpha);
        graphics.fill();

        graphics.beginPath();
        graphics.arc(particle.x, particle.y, particle.r * 0.3, 0, Math.PI * 2);
        graphics.fillStyle = tc(Math.min(1, alpha * 1.5));
        graphics.fill();

        particle.y -= particle.speedY * frameSteps;
        particle.x += particle.drift * frameSteps;
        particle.rot += particle.rotSpeed * frameSteps;
        if (particle.y < -10) {
          particle.y = surface.height + 10;
          particle.x = Math.random() * surface.width;
        }
        if (particle.x < -10 || particle.x > surface.width + 10) {
          particle.x = Math.random() * surface.width;
        }
      }
    });

    return () => {
      stopAnimationLoop();
      window.removeEventListener("resize", onResize);
    };
  }, [hue, theme, isMobile, frameStride]);

  return <Box component="canvas" ref={canvasRef} sx={getCanvasAnimationLayerSx(variant)} />;
};
